import React from 'react';
import PropTypes from 'prop-types';
import '../../../TeamManager/styles/teamManagerStyles.css';
import { useMembershipProductShowContext } from '../../startup/MembershipProductShowApp';
import { DISPLAY, DOOR_ADMISSION, NO_ORGANIZER_FEES, isDoorAdmission, isNoOrganizerFees } from '../../membershipFormLogic';

const VENUE_BADGE_COLORS = ['blue', 'red', 'yellow', 'green', 'purple', 'orange'];

function VenueChips({ venues }) {
  if (!venues.length) return <span className="text-muted">None</span>;
  return (
    <div className="team-venue-badges">
      {venues.map((venue, index) => (
        <span key={venue.id} className="team-venue-badge">
          <span className={`team-venue-dot team-venue-dot-${VENUE_BADGE_COLORS[index % VENUE_BADGE_COLORS.length]}`}></span>
          <span>{venue.name}</span>
        </span>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="small font-weight-medium text-uppercase text-muted mb-2" style={{ letterSpacing: 0.3 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function BenefitsList({ benefits }) {
  if (!benefits.length) {
    return <span className="text-muted">No benefits configured.</span>;
  }
  return (
    <ul className="mb-0 pl-3">
      {benefits.map((benefit) => (
        <li key={benefit.id} className="mb-1">
          {benefit.description}
          {isDoorAdmission(benefit) ? (
            <span className="text-muted">{` · Door admission, ${benefit.scans_per_day} ${benefit.scans_per_day === 1 ? "scan" : "scans"} a day`}</span>
          ) : null}
          {isNoOrganizerFees(benefit) ? <span className="text-muted">{" · No organizer fees for members"}</span> : null}
        </li>
      ))}
    </ul>
  );
}

BenefitsList.propTypes = {
  benefits: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      kind: PropTypes.oneOf([DISPLAY, DOOR_ADMISSION, NO_ORGANIZER_FEES]).isRequired,
      scans_per_day: PropTypes.number,
    })
  ).isRequired,
};

export default function SettingsPanel() {
  const { membershipProduct, venueOwnerships = [] } = useMembershipProductShowContext();
  const associatedIds = (membershipProduct.venue_ownerships || []).map((vo) => vo.id);
  const associatedVenues = venueOwnerships.filter((vo) => associatedIds.includes(vo.id));
  const billingVenue = venueOwnerships.find((vo) => vo.id === membershipProduct.billing_venue_ownership_id);
  const benefits = membershipProduct.membership_product_benefits || [];

  return (
    <div className="team-member-card mb-3 h-100">
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Settings</h2>
          </div>
        </div>
      </div>
      <div className="team-member-card-body">
        <div className="row">
          <div className="col-md-6 mb-3">
            <Field label="Venues">
              <VenueChips venues={associatedVenues} />
            </Field>
          </div>
          <div className="col-md-6 mb-3">
            <Field label="Billing Venue">
              {billingVenue ? (
                <span>{billingVenue.name}</span>
              ) : (
                <span className="text-muted">Not set</span>
              )}
            </Field>
          </div>
          <div className="col-md-6 mb-3">
            <Field label="Membership Name">
              <span>{membershipProduct.name}</span>
            </Field>
          </div>
          <div className="col-md-6 mb-3">
            <Field label="Description">
              <span className="text-break">
                {membershipProduct.description || <span className="text-muted">—</span>}
              </span>
            </Field>
          </div>
          <div className="col-12">
            <Field label="Benefits &amp; Perks">
              <BenefitsList benefits={benefits} />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}
